'use client';

import { useEffect } from 'react';
import { DevEvents } from '../@types';
import { BigNum } from '@velocity-exchange/sdk';
import useVelocityStore from '../stores/VelocityStore/useVelocityStore';
import { VelocityWindow } from 'src/window/velocityWindow';
import { useSpotMarketsLookup } from './spotMarkets/useSpotMarketsLookup';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { cleanupExpiredOverrides } from 'src/utils/dev/devLiquidationOverrides';
import useForceUpdate from './useForceUpdate';
import { useRenameSubaccount } from './velocityActions/user/useRenameSubaccount';
import { useReclaimRent } from './velocityActions/user/useReclaimRent';
import { useWithdrawCollateral } from './velocityActions/spot-management/useWithdrawCollateral';
import { useDepositCollateral } from './velocityActions/spot-management/useDepositCollateral';
import { useWithdrawDustPositions } from './velocityActions/settlement/useWithdrawDustPositions';
import { useCancelVaultWithdrawal } from './velocityActions/vaults/useCancelVaultWithdrawal';

const ALREADY_SETUP_REF = { current: false };

// # Type for event handlers for DevEvents interface .. [key, handler]
type EventHandlerConfig = [keyof DevEvents, DevEvents[keyof DevEvents]];

const useDevActions = () => {
	const spotMarketsLookup = useSpotMarketsLookup();
	const triggerRefresh = useForceUpdate();

	const getState = useVelocityStore((s) => s.get);
	const getVelocityClient = () => getState().velocityClient.client;

	const { renameSubaccount } = useRenameSubaccount();
	const { reclaimRent } = useReclaimRent();
	const { withdrawCollateral } = useWithdrawCollateral();
	const { depositCollateralForExistingAccount } = useDepositCollateral();
	const { withdrawDustPositions } = useWithdrawDustPositions();
	const { cancelVaultWithdrawalRequest } = useCancelVaultWithdrawal();

	// # Logic for each event handler
	const handleWithdrawAllDust: DevEvents['withdraw_all_dust'] = () => {
		withdrawDustPositions();
	};

	const handleSettleAllValidOracleMarkets: DevEvents['settle_all_valid_oracle_markets'] =
		() => {};

	const handleWithdraw5Usdc: DevEvents['withdraw_5_usdc'] = () => {
		const USDCMarket = spotMarketsLookup[0];

		withdrawCollateral({
			amount: BigNum.fromPrint('5', USDCMarket.precisionExp),
			spotMarketConfig: USDCMarket,
		});
	};

	const createAnchorErrorTx: DevEvents['force_anchor_error_tx'] = () => {
		renameSubaccount(1_000, 'bler');
	};

	const createVelocityErrorTx: DevEvents['force_drift_error_tx'] = () => {
		const velocityClient = getVelocityClient();
		const userAccount = velocityClient?.getUserAccount(
			velocityClient.activeSubAccountId
		);
		const vaultClient = useVaultsStore.getState().vaultClient;
		if (!userAccount || !vaultClient) return;

		cancelVaultWithdrawalRequest(DEFAULT_PUBLIC_KEY, vaultClient);
	};

	const createWalletbalanceError: DevEvents['force_insufficient_wallet_balance_error'] =
		() => {
			const spotMarket = spotMarketsLookup[0];

			depositCollateralForExistingAccount(
				BigNum.fromPrint((50_000_000).toString(), spotMarket.precisionExp),
				spotMarket
			);
		};

	const handleReclaimRent: DevEvents['reclaim_rent'] = () => {
		const velocityClient = getVelocityClient();
		if (!velocityClient || velocityClient.activeSubAccountId === undefined)
			return;

		reclaimRent(velocityClient.activeSubAccountId);
	};

	// # Config for event handlers
	const eventHandlerConfigs: EventHandlerConfig[] = [
		['withdraw_all_dust', handleWithdrawAllDust],
		['settle_all_valid_oracle_markets', handleSettleAllValidOracleMarkets],
		['withdraw_5_usdc', handleWithdraw5Usdc],
		['force_anchor_error_tx', createAnchorErrorTx],
		['reclaim_rent', handleReclaimRent],
		['force_drift_error_tx', createVelocityErrorTx],
		['force_insufficient_wallet_balance_error', createWalletbalanceError],
	];

	// # Connect event handlers to the event emitter
	useEffect(() => {
		if (ALREADY_SETUP_REF.current) return;

		ALREADY_SETUP_REF.current = true;

		const devEventEmitter = VelocityWindow.devEventEmitter;

		eventHandlerConfigs.forEach(([key, handler]) => {
			devEventEmitter.on(key, handler);
		});

		return () => {
			eventHandlerConfigs.forEach(([key, handler]) => {
				devEventEmitter.off(key, handler);
			});
			ALREADY_SETUP_REF.current = false;
		};
	}, []);

	// Cleanup expired liquidation overrides periodically
	useEffect(() => {
		const interval = setInterval(() => {
			const removed = cleanupExpiredOverrides();
			if (removed) {
				triggerRefresh();
			}
		}, 1000); // Check every second

		return () => {
			clearInterval(interval);
		};
	}, [triggerRefresh]);
};

export default useDevActions;
